import { BOT_DIFFICULTIES, type BotDifficulty } from "../../bot/difficulty";
import type { Player, TransitionEvent } from "../../game";
import {
  MATCH_RECORDING_SCHEMA_VERSION,
  type MatchHistorySummary,
  type MatchRecordV1,
  type MatchReplay,
} from "../match/persistence";
import { parseMatchRecord, replayRecording } from "../match/persistence";
import { canonicalRecordJson } from "../match/persistence";

export const HISTORY_BUNDLE_FORMAT = "passo-history" as const;
export const HISTORY_BUNDLE_VERSION = 1 as const;
export const MAX_HISTORY_IMPORT_BYTES = 20 * 1024 * 1024;
export const MAX_HISTORY_IMPORT_RECORDS = 10_000;
export const MAX_HISTORY_IMPORT_MOVES_PER_RECORD = 10_000;
export const MAX_HISTORY_IMPORT_MOVES_TOTAL = 250_000;

export type HistoryBundleV1 = Readonly<{
  format: typeof HISTORY_BUNDLE_FORMAT;
  schemaVersion: typeof HISTORY_BUNDLE_VERSION;
  exportedAt: number;
  matches: readonly MatchRecordV1[];
}>;

export type HistoryImportResult =
  | Readonly<{ ok: true; records: readonly MatchRecordV1[] }>
  | Readonly<{ ok: false; message: string }>;

export type BotRecord = Readonly<{ wins: number; losses: number; draws: number }>;

export type HistoryStatistics = Readonly<{
  botGames: number;
  record: BotRecord;
  winPercentage: number | null;
  currentStreak: number;
  longestStreak: number;
  averageMoves: number | null;
  recent: readonly ("win" | "loss" | "draw")[];
  byDifficulty: Readonly<Record<BotDifficulty, BotRecord>>;
  byColor: Readonly<Record<Player, BotRecord>>;
  localGames: number;
}>;

export function createHistoryBundle(
  records: readonly MatchRecordV1[],
  exportedAt = Date.now(),
): HistoryBundleV1 {
  return Object.freeze({
    format: HISTORY_BUNDLE_FORMAT,
    schemaVersion: HISTORY_BUNDLE_VERSION,
    exportedAt,
    matches: Object.freeze([...records]),
  });
}

export async function parseHistoryBundle(text: string): Promise<HistoryImportResult> {
  if (new TextEncoder().encode(text).byteLength > MAX_HISTORY_IMPORT_BYTES)
    return invalid("History file is larger than 20 MiB.");
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    return invalid("History file is not valid JSON.");
  }
  if (
    !isRecord(value) ||
    !hasExactlyKeys(value, ["format", "schemaVersion", "exportedAt", "matches"]) ||
    value["format"] !== HISTORY_BUNDLE_FORMAT ||
    value["schemaVersion"] !== HISTORY_BUNDLE_VERSION ||
    !Number.isSafeInteger(value["exportedAt"]) ||
    Number(value["exportedAt"]) < 0 ||
    !Array.isArray(value["matches"])
  ) {
    return invalid("History file format is not supported.");
  }
  const matches = value["matches"] as readonly unknown[];
  if (matches.length > MAX_HISTORY_IMPORT_RECORDS)
    return invalid("History file contains too many matches.");
  let totalMoves = 0;
  for (let index = 0; index < matches.length; index += 1) {
    const candidate = matches[index];
    if (!isRecord(candidate) || !Array.isArray(candidate["moves"]))
      return invalid(`Match ${index + 1}: Match record fields are malformed.`);
    if (candidate["moves"].length > MAX_HISTORY_IMPORT_MOVES_PER_RECORD)
      return invalid(`Match ${index + 1} contains too many moves.`);
    totalMoves += candidate["moves"].length;
    if (totalMoves > MAX_HISTORY_IMPORT_MOVES_TOTAL)
      return invalid("History file contains too many total moves.");
    if ((index + 1) % 250 === 0) await yieldToBrowser();
  }
  const records: MatchRecordV1[] = [];
  const byId = new Map<string, MatchRecordV1>();
  for (let index = 0; index < matches.length; index += 1) {
    const parsed = parseMatchRecord(matches[index]);
    if (!parsed.ok) return invalid(`Match ${index + 1}: ${parsed.message}`);
    const duplicate = byId.get(parsed.record.id);
    if (duplicate !== undefined) {
      if (canonicalRecordJson(duplicate) !== canonicalRecordJson(parsed.record))
        return invalid(`Match ${index + 1} conflicts with another record in the file.`);
    } else {
      byId.set(parsed.record.id, parsed.record);
      records.push(parsed.record);
    }
    if ((index + 1) % 25 === 0) await yieldToBrowser();
  }
  return Object.freeze({ ok: true, records: Object.freeze(records) });
}

export function replayMatchRecord(record: MatchRecordV1): MatchReplay {
  const replay = replayRecording({
    schemaVersion: MATCH_RECORDING_SCHEMA_VERSION,
    id: record.id,
    rulesVersion: record.rulesVersion,
    initialGame: record.initialGame,
    moves: record.moves,
    completedAt: record.completedAt,
  });
  if (replay === null) throw new TypeError("Validated match record could not be replayed.");
  return replay;
}

export function deriveHistoryStatistics(
  summaries: readonly MatchHistorySummary[],
): HistoryStatistics {
  const completed = summaries.filter((summary) => summary.result.kind !== "abandoned");
  const bots = completed.filter((summary) => summary.mode === "bot");
  const record = countRecord(bots);
  const byDifficulty = Object.fromEntries(
    BOT_DIFFICULTIES.map((difficulty) => [
      difficulty,
      countRecord(bots.filter((summary) => summary.difficulty === difficulty)),
    ]),
  ) as Record<BotDifficulty, BotRecord>;
  const byColor = Object.fromEntries(
    (["black", "red"] as const).map((color) => [
      color,
      countRecord(bots.filter((summary) => summary.humanPlayer === color)),
    ]),
  ) as Record<Player, BotRecord>;
  const outcomes = bots.map(botPerspective);
  let currentStreak = 0;
  for (const outcome of outcomes) {
    if (outcome !== "win") break;
    currentStreak += 1;
  }
  let longestStreak = 0;
  let streak = 0;
  for (const outcome of [...outcomes].reverse()) {
    streak = outcome === "win" ? streak + 1 : 0;
    longestStreak = Math.max(longestStreak, streak);
  }
  return Object.freeze({
    botGames: bots.length,
    record,
    winPercentage: bots.length === 0 ? null : Math.round((record.wins / bots.length) * 100),
    currentStreak,
    longestStreak,
    averageMoves:
      bots.length === 0 ? null : bots.reduce((sum, item) => sum + item.moveCount, 0) / bots.length,
    recent: Object.freeze(outcomes.slice(0, 10)),
    byDifficulty: Object.freeze(byDifficulty),
    byColor: Object.freeze(byColor),
    localGames: completed.filter((summary) => summary.mode === "local").length,
  });
}

export function eventBadges(events: readonly TransitionEvent[]): readonly string[] {
  const badges = new Set<string>();
  for (const event of events) {
    if (event.kind === "disc-moved") badges.add("Move");
    else if (event.kind === "field-removed") badges.add("Field removed");
    else if (event.kind === "orphan-cleanup") badges.add("Orphan cleanup");
    else if (event.kind === "disc-exited") badges.add("Passage");
    else if (event.kind === "game-ended") badges.add("Game ended");
  }
  return Object.freeze([...badges]);
}

function countRecord(items: readonly MatchHistorySummary[]): BotRecord {
  let wins = 0;
  let losses = 0;
  let draws = 0;
  for (const item of items) {
    const outcome = botPerspective(item);
    if (outcome === "win") wins += 1;
    else if (outcome === "loss") losses += 1;
    else draws += 1;
  }
  return Object.freeze({ wins, losses, draws });
}

export function botPerspective(summary: MatchHistorySummary): "win" | "loss" | "draw" {
  if (summary.result.kind === "abandoned") {
    throw new TypeError("An abandoned match has no competitive result.");
  }
  if (
    summary.result.kind === "manual-draw" ||
    summary.result.kind === "repetition-draw" ||
    summary.mode !== "bot"
  ) {
    return "draw";
  }
  return summary.result.winner === summary.humanPlayer ? "win" : "loss";
}

function invalid(message: string): HistoryImportResult {
  return Object.freeze({ ok: false, message });
}
function yieldToBrowser(): Promise<void> {
  return new Promise((resolve) => globalThis.setTimeout(resolve, 0));
}
function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function hasExactlyKeys(
  record: Readonly<Record<string, unknown>>,
  keys: readonly string[],
): boolean {
  const actual = Object.keys(record).sort();
  const expected = [...keys].sort();
  return actual.length === expected.length && actual.every((key, index) => key === expected[index]);
}
